import type { ReferenceWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { fetchGraphemesByGlyphsForStrokes } from "#fetch-graphemes-by-glyphs-for-strokes";
import { formatStrokeCount } from "#format-stroke-count";
import arrayLength from "@-/com.npmjs.extra-array__length";
import {
  GraphemeSvgGlyphStripPlayer,
  type GraphemeSvgGlyphStripCell,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

/**
 * A word's stroke order as a surface: the total stroke count in the header, and the whole word
 * written on one strip of practice paper (with a Replay button) as the body.
 */
export function MorphemeInfoPanelCaligraphyWordStrokesSurface({
  className,
  wordCard,
}: {
  className?: string;
  wordCard: ReferenceWordMorphemeSlice;
}) {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { isActive } = useLearnCardSurface();
  const getGraphemeGlyph = useGetGraphemeGlyph();

  const characters = useMemo(
    () => wordCard.characters.map((character) => character.grapheme),
    [wordCard.characters],
  );
  const { data: strokeListByCharacter } = useQuery({
    queryFn: () => fetchGraphemesByGlyphsForStrokes(staticDb, characters),
    queryKey: ["word-caligraphy-strokes", characters],
  });

  const cellList = useMemo<readonly GraphemeSvgGlyphStripCell[] | null>(
    () =>
      strokeListByCharacter === undefined
        ? null
        : characters.map((character) => ({
            glyph: getGraphemeGlyph(character),
            strokeList: strokeListByCharacter[character] ?? null,
          })),
    [characters, getGraphemeGlyph, strokeListByCharacter],
  );

  const totalStrokes = useMemo(
    () => cellList?.reduce((sum, { strokeList }) => sum + arrayLength(strokeList ?? []), 0) ?? 0,
    [cellList],
  );

  const [replayKey, setReplayKey] = useState(0);

  return (
    <Surface className={className} testID="caligraphy-word-strokes-surface">
      <SurfaceHeader className="flex-row items-center justify-between gap-2 py-2">
        <SurfaceTitle className="pt-0">Strokes</SurfaceTitle>
        {totalStrokes > 0 && (
          <Text className="text-xs" colorVariant="muted">
            {formatStrokeCount(totalStrokes)}
          </Text>
        )}
      </SurfaceHeader>
      <SurfaceContent className="p-3">
        {cellList === null ? (
          <View className="h-24 items-center justify-center">
            <ActivityIndicator />
          </View>
        ) : (
          <GraphemeSvgGlyphStripPlayer
            autoPlay={isActive}
            cellList={cellList}
            replayKey={replayKey}
          />
        )}
        <Pressable
          accessibilityLabel="Replay all strokes"
          accessibilityRole="button"
          className="mt-3 h-9 justify-center self-center rounded-xs border border-border px-4 active:bg-accent"
          onPress={() => setReplayKey((previousKey) => previousKey + 1)}
        >
          <View className="flex-row items-center gap-2">
            <Icon className="text-foreground" name="LuPenLine" size={14} />
            <Text className="text-sm text-foreground">Replay</Text>
          </View>
        </Pressable>
      </SurfaceContent>
    </Surface>
  );
}
